import { createAsyncThunk } from "@reduxjs/toolkit";
import { IStoreState } from "App/types";
import { has, map } from "lodash-es";
import { Dispatch } from "react";

import { IDataKeys, IInfoBlock } from "shared/types";
import { InfoBlockService } from "Venues/services";
import { VENUE_ACTIONS, IInfoBlockWithPath, IInfoBlockId } from "Venues/types";

export const dispatchGetAllInfoBlocksAction = (
  dispatch: Dispatch<any>,
  infoBlocks: IInfoBlockId[],
  parentType: "venue" | "stage"
) => {
  const action =
    parentType === "stage" ? VENUE_ACTIONS.getStageInfoBlocks : VENUE_ACTIONS.getVenueInfoBlocks;
  return dispatch(getAllInfoBlocks({ infoBlocks, action }));
};

export const getAllInfoBlocks = createAsyncThunk(
  VENUE_ACTIONS.getInfoBlocks,
  async (
    { infoBlocks }: { infoBlocks: IInfoBlockId[]; action: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      await Promise.all(
        map(infoBlocks, async ({ infoBlock }) => {
          infoBlock && (await dispatch(getInfoBlock(infoBlock.data.id)));
        })
      );
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getInfoBlock = createAsyncThunk(
  VENUE_ACTIONS.getInfoBlock,
  async (path: string, { rejectWithValue }) => {
    try {
      return InfoBlockService.getInfoBlock(path);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  // Do not fetch the info block if it is already in the state.
  // The info blocks state gets updated on delete/edit actions
  {
    condition: (path, { getState }) => {
      const state = getState() as IStoreState;
      const { stageInfoBlock, venueInfoBlock } = state.data.data;
      const infoBlocks = path.includes("stage_info_blocks") ? stageInfoBlock : venueInfoBlock;

      return !has(infoBlocks, path);
    },
  }
);

export const createInfoBlock = createAsyncThunk(
  VENUE_ACTIONS.createInfoBlock,
  async (infoBlock: IInfoBlock, { rejectWithValue }) => {
    try {
      return await InfoBlockService.createInfoBlock(infoBlock);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editInfoBlock = createAsyncThunk(
  VENUE_ACTIONS.editInfoBlock,
  async (infoBlock: IInfoBlockWithPath, { rejectWithValue }) => {
    try {
      return await InfoBlockService.editInfoBlock(infoBlock);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export interface DeleteInfoBlockActionProps {
  venue?: string | { id: string };
  stage?: string | { id: string };
  type: string;
  id: string;
}

export const deleteInfoBlock = createAsyncThunk(
  VENUE_ACTIONS.deleteInfoBlock,
  async ({ stage, venue, type, id }: DeleteInfoBlockActionProps, { rejectWithValue }) => {
    try {
      const path = InfoBlockService.getInfoBlockApiPath(stage ? "stage" : "venue", id);

      await InfoBlockService.deleteInfoBlock(path);
      return {
        venue,
        stage,
        type,
        path,
        dataKey: stage ? IDataKeys.stageInfoBlock : IDataKeys.venueInfoBlock,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
